import { useCallback, useMemo } from 'react';
import { useRecoilCallback, useRecoilValue } from 'recoil';
import { ColumnShape } from 'react-base-table';

import { LogEntryEdge } from 'common/graphqlTypes';
import { PlaceholderLogEntryEdge } from 'hooks/useLRQ/useEventCache';
import BaseTable, { BaseTableProps } from 'components/common/BaseTable/BaseTable';
import {
  searchPageRemainingWidthSelector,
  wrappedLinesScrollbarWidthState,
  wrappedLineWidthSelector,
} from 'components/common/Managers/LayoutStateManager';
import { timezoneState } from 'components/common/Managers/PreferencesManager.state';
import { settingsState } from 'components/common/Managers/SettingsManager.state';
import WrappedLogLine from 'components/search/SearchLogsPage/WrappedLogResults/WrappedLogLine';
import { useLogResultsState, useScrollPositioningHandler } from 'components/search/SearchLogsPage/LogResultsContainer.hooks';

import './WrappedLogResults.scss';

/**
 * This component displays events as multi-line wrapped rows of content within an "infinite" scrolling list that loads
 * additional events as the user scrolls.
 *
 * @prop height             height in pixels reserved for this component
 * @prop logIds             list of continuationToken ids representing the events to display
 */

type WrappedLogResultsProps = {
  height: number;
  logIds: string[];
};

/**
 * PureWrappedLogResults
 */
export function PureWrappedLogResults({ ...rest }: BaseTableProps<LogEntryEdge | PlaceholderLogEntryEdge>): JSX.Element {
  return <BaseTable headerHeight={0} {...rest} />;
}
/**
 * WrappedLogResults
 */

function WrappedLogResults(props: WrappedLogResultsProps): JSX.Element {
  // TODO: consider using  flex layout (which is already in place) + AutoResizer instead ("initial measurement approach" may not work)
  const { height, logIds = [] } = props;
  const lineWidth = useRecoilValue(wrappedLineWidthSelector);
  const tableWidth = useRecoilValue(searchPageRemainingWidthSelector);
  const settings = useRecoilValue(settingsState);
  const timezone = useRecoilValue(timezoneState);
  const { onItemsRendered, scrollOffset, itemData: { filter, logEntryEdges, selectedToken } } = useLogResultsState();
  const baseTableRef = useScrollPositioningHandler<LogEntryEdge | PlaceholderLogEntryEdge>(scrollOffset, logIds);

  /**
   * Prepare columns
   */
  const columns: ColumnShape<LogEntryEdge | PlaceholderLogEntryEdge>[] = useMemo(() => [{
    key: 'wrapped-line-col',
    className: 'BaseTable__WrappedLinesCell',
    width: lineWidth,
    cellRenderer: ({ rowIndex }: { rowIndex: number }): JSX.Element => (
      <WrappedLogLine
        data={{
          filter, logEntryEdges, settings, timezone, selectedToken,
        }}
        index={rowIndex}
        style={{ width: lineWidth }}
      />
    ),
  }], [lineWidth, filter, logEntryEdges, settings, timezone, selectedToken]);

  /**
   * Action Handlers
   */
  const onRowsRendered = useCallback(({ startIndex, stopIndex }) => onItemsRendered({ visibleStartIndex: startIndex, visibleStopIndex: stopIndex }), [onItemsRendered]);
  const onScrollbarPresenceChange = useRecoilCallback(({ set }) => ({ size, vertical }): void => set(wrappedLinesScrollbarWidthState, (vertical && size) || 0), []);

  return (
    <PureWrappedLogResults
      forwardedRef={baseTableRef}
      fixed
      width={tableWidth}
      height={height}
      ignoreFunctionInColumnCompare={false}
      data={logEntryEdges}
      columns={columns}
      rowKey="continuationToken"
      estimatedRowHeight={30}
      onRowsRendered={onRowsRendered}
      onScrollbarPresenceChange={onScrollbarPresenceChange}
    />
  );
}

export default WrappedLogResults;
